import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import { Modal } from '../models/modal';
import { Modalable, ResponseStatus } from '../models/modalable';
import { Subject, Subscription } from 'rxjs';
import { DisabledFeatureModalable } from '../models/disabled-feature-modalable';
import { QuickLink, QuickLinkType } from '../models/quick-link';
import { Link } from '../models/link';
import { AssignmentsLink } from '../models/assignments-link';
import { HomeworkLink } from '../models/homework-link';
import { ModalableWhiteboardDrawing } from '../models/modalable-whiteboard-drawing';
import { ModalableWhiteboardVideo } from '../models/modalable-whiteboard-video';
import { ModalableYedo } from '../models/modalable-yedo';
import { ModalableAccessCode } from '../models/modalable-access-code';
import { Materials } from '../models/materials';
import { HomeworkExercisePreviewModalable } from '../models/homework-exercise-preview-modalable';
import { ApplicationStateService } from './application-state.service';
import {
    WelcomeModalComponent
} from '../components/bookviewer/pages/modal-container/welcome-modal/welcome-modal.component';
import {
    MyBooksModalComponent
} from '../components/bookviewer/pages/modal-container/my-books-modal/my-books-modal.component';
import {
    AboutThisBookModalComponent
} from '../components/bookviewer/pages/modal-container/about-this-book-modal/about-this-book-modal.component';
import {
    OfflineModeModalComponent
} from '../components/bookviewer/pages/modal-container/offline-mode-modal/offline-mode-modal.component';
import {
    PlatformLinkCopiedConfirmationModalComponent
// eslint-disable-next-line max-len
} from '../components/bookviewer/pages/modal-container/platform-link-copied-confirmation-modal/platform-link-copied-confirmation-modal.component';
import {
    ClassSharingModalComponent
} from '../components/bookviewer/pages/modal-container/class-sharing-modal/class-sharing-modal.component';
import { Video } from '../models/video';
import { Audio } from '../models/audio';
import { UrlWhitelist } from '../../environments/configs/url-whitelist';
import { UrlHelper } from '../utils/url';
import {
    BuyBookModalComponent
} from '../components/bookviewer/pages/modal-container/buy-book-modal/buy-book-modal.component';
import { DigiTrainerContent } from '../models/digi-trainer-content';
import { ModalComponent } from '../models/modal-component';
import {
    RedirectModalComponent
} from '../components/bookviewer/pages/modal-container/redirect-modal/redirect-modal.component';
import { VideoModalComponent } from '../components/bookviewer/pages/modal-container/video-modal/video-modal.component';
import {
    OfflineVersionModalComponent
} from '../components/bookviewer/pages/modal-container/offline-version-modal/offline-version-modal.component';
import {
    NoInternetModalComponent
} from '../components/bookviewer/pages/modal-container/no-internet-modal/no-internet-modal.component';
import { AccessService } from './access.service';
import { ModalableEdusuite } from '../models/modalable-edusuite';
import {
    FeatureDisabledComponent
} from '../components/bookviewer/pages/modal-container/feature-disabled/feature-disabled.component';
import {
    AccessibilityModalComponent
} from '../components/bookviewer/pages/modal-container/accessibility-modal/accessibility-modal.component';
import { ModalableDigbi } from '../models/modalable-digbi';

declare const $;

@Injectable({
    providedIn: 'root'
})
/**
 * 2 different approaches to a modal:
 *  - MODALABLE & MODAL COMPONENT (openModalable): to pass a to a Modalable Entity (modal's data) with Modal ID
 *  - MODAL COMPONENT (openModal): only a static component to open Modal by ID without any additional data
 */
export class ModalService {
    static E_SQUIRREL = QuickLinkType.E_SQUIRREL;
    static GMT = QuickLinkType.GMT;
    static HSM = QuickLinkType.HSM;
    static OTHER = QuickLinkType.OTHER;
    static DIGI = QuickLinkType.DIGI;
    static YEDO = QuickLinkType.YEDO;
    static EDU_SUITE = QuickLinkType.EDU_SUITE;
    static MATHION = QuickLinkType.MATHION;
    static DIGBI = QuickLinkType.DIGBI;
    static MATHE_GYM = QuickLinkType.MATHE_GYM;
    static MATHE4ALLE = QuickLinkType.MATHE4ALLE;
    static PHASE = QuickLinkType.PHASE;
    static CALCUFLOW = QuickLinkType.CALCUFLOW;
    static SIA = QuickLinkType.SIA;
    static MATERIALS = 'MATERIALS';
    static LINK = 'LINK';
    static VIDEO = 'VIDEO';
    static AUDIO = 'AUDIO';
    static SCHOOL_BAG = 'SCHOOL_BAG';
    static EXPORT_PAGES = 'EXPORT_PAGES';
    static HIGHLIGHT_TEXT = 'highlightText';
    static CREATE_NOTE = 'createNote';
    static COPY_TEXT = 'copyText';
    static PAGE_FAVORITE = 'PAGE_FAVORITE';
    static NOTES = 'NOTES';
    static SEARCH = 'SEARCH';
    static DRAW_MODE = 'DRAWMODE';
    static TEST_GENERATOR = 'TEST_GENERATOR';

    static DIGI_TRAINER_LINK = 'digiTrainer';
    static HSM_SIDEBAR_LINK = 'hsmSidebar';
    static HOMEWORK_EXERCISE_PREVIEW = 'homeworkExercisePreview';
    modals: Map<string, Modal>;
    disabledModalable: Modalable;
    subscription: Subscription;
    private openModalSubject = new Subject<Modalable>();
    openModalObservable = this.openModalSubject.asObservable();
    private accessService = inject(AccessService);
    triggerElement: HTMLElement|null = null;
    private environmentInjector = inject(EnvironmentInjector);

    constructor(public applicationStateService: ApplicationStateService) {
        this.subscription = this.openModalObservable.subscribe(modalable => {
            if (
                (
                    !modalable.canBypassCheck() &&
                    !this.applicationStateService.userData.bookAccessCode &&
                    !(modalable instanceof HomeworkExercisePreviewModalable)
                ) ||
                (
                    !modalable.canBypassCheck() &&
                    (modalable instanceof ModalableYedo || modalable instanceof DisabledFeatureModalable)
                ) ||
                (
                    !modalable.canBypassCheck() &&
                    this.applicationStateService.userData.bookAccessCode &&
                    !this.accessService.isPlus(this.applicationStateService.userData.bookAccessCode.version) &&
                    this.checkPremiumPlus(modalable)
                ) ||
                (
                    !modalable.canBypassCheck() &&
                    this.applicationStateService.userData.bookAccessCode &&
                    this.accessService.isBasic(this.applicationStateService.userData.bookAccessCode.version) &&
                    this.checkNormal(modalable)
                )
            ) {
                this.disabledModalable = modalable;
                setTimeout(() => {
                    this.addAnimation(DisabledFeatureModalable.MODAL_ID);
                    this.openModalById(DisabledFeatureModalable.MODAL_ID);
                }, 0);
            } else {
                this.disabledModalable = undefined;
                setTimeout(() => {
                    this.addAnimation(modalable.getModalId());
                    this.openModalById(modalable.getModalId());
                }, 0);

            }
        });

        this.modals = new Map<string, Modal>();
        this.registerModal(DisabledFeatureModalable.MODAL_ID);
        this.registerModal(WelcomeModalComponent.MODAL_ID);
        this.registerModal(MyBooksModalComponent.MODAL_ID);
        this.registerModal(AboutThisBookModalComponent.MODAL_ID);
        this.registerModal(OfflineModeModalComponent.MODAL_ID);
        this.registerModal(PlatformLinkCopiedConfirmationModalComponent.MODAL_ID);
        this.registerModal(ClassSharingModalComponent.MODAL_ID);
        this.registerModal(DigiTrainerContent.MODAL_ID);
        this.registerModal(HomeworkExercisePreviewModalable.MODAL_ID);
        this.registerModal(Link.MODAL_ID);
        this.registerModal(QuickLink.MODAL_ID);
        this.registerModal(AssignmentsLink.MODAL_ID);
        this.registerModal(HomeworkLink.MODAL_ID);
        this.registerModal(ModalableWhiteboardDrawing.MODAL_ID);
        this.registerModal(ModalableWhiteboardVideo.MODAL_ID);
        this.registerModal(ModalableYedo.MODAL_ID);
        this.registerModal(ModalableEdusuite.MODAL_ID);
        this.registerModal(ModalableDigbi.MODAL_ID);
        this.registerModal(ModalableAccessCode.MODAL_ID);
        this.registerModal(Materials.MODAL_ID);
        this.registerModal(BuyBookModalComponent.MODAL_ID);
        this.registerModal(RedirectModalComponent.MODAL_ID);
        this.registerModal(VideoModalComponent.MODAL_ID);
        this.registerModal(OfflineVersionModalComponent.MODAL_ID);
        this.registerModal(NoInternetModalComponent.MODAL_ID);
        this.registerModal(AccessibilityModalComponent.MODAL_ID);

        window.document.addEventListener('closeHSMModal', () => {
            this.closeModal(AssignmentsLink.MODAL_ID);
        }, false);
    }

    registerModal(id: string): void {
        const modal = new Modal(id, {}, 'bounceInDown');
        this.modals.set(modal.getId(), modal);

        if (modal.getAnimation()) {
            this.addAnimation(modal.getId());
        }
    }

    openModal<T extends new(...args: Array<any>) => ModalComponent>(component: T | Modal | ModalComponent | Modalable): void {
        this.triggerElement = document.activeElement as HTMLElement;
        if (component instanceof Function) {
            // @ts-ignore
            component = runInInjectionContext(this.environmentInjector, () => new component());
        }
        if (component instanceof ModalComponent || component instanceof Modal) {
            // @ts-ignore
            this.openModalById(component.MODAL_ID || component.getId());
        } else {
            this.openModalable(component as Modalable);
        }
    }

    closeModal(modalId: string): void {
        if (!this.modals.has(modalId)) {
            console.error('Modal not registered');
        }
        const modal = this.modals.get(modalId);
        const modalElement = ($(`#${modal.getId()}`));
        modalElement.modal('hide');
        modal.setOpen(false);
    }

    addAnimation(modalId: string, animation?: string): void {

        if (!this.modals.has(modalId)) {
            console.error('Modal not found');
        }
        const modal = this.modals.get(modalId);
        $(`#${modal.getId()}`).on('show.bs.modal', function (): void {
            // eslint-disable-next-line no-invalid-this
            const modalDialog = $(this).find('.modal-dialog');
            modalDialog.addClass('animated');
            if (animation) {
                modalDialog.addClass(animation);
            } else {
                modalDialog.addClass(modal.getAnimation());
            }
        });
    }

    openDisabledFeatureModal(): void {
        this.openModal(FeatureDisabledComponent);
    }

    openWelcomeModal(): void {
        this.openModal(WelcomeModalComponent);
    }

    openDisabledNotesModal(): void {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.NOTES;
        disabledFeatureModal.iconUrl = 'sidebar/sidebar-icon-notizen.png';
        this.openModal(disabledFeatureModal);
    }

    openDisabledHSMModal(): void {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.HSM;
        disabledFeatureModal.iconUrl = 'sidebar/sidebar-icon-hsm.png';
        this.openModal(disabledFeatureModal);
    }

    openDisabledDigiTrainerModal(): void {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.DIGI_TRAINER_LINK;
        disabledFeatureModal.iconUrl = 'sidebar/sidebar-digitrainer.png';
        this.openModal(disabledFeatureModal);
    }

    openDisabledEduSuiteModal(status: ResponseStatus) {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.EDU_SUITE;
        disabledFeatureModal.iconUrl = 'sidebar-quicklinks/edusuite-logo.png';
        disabledFeatureModal.status = status;
        this.openModal(disabledFeatureModal);
    }

    openDisabledSearchModal(): void {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.SEARCH;
        disabledFeatureModal.iconUrl = 'tab-icon-search.png';
        this.openModal(disabledFeatureModal);
    }

    openDisabledTestGeneratorModal(): void {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.TEST_GENERATOR;
        disabledFeatureModal.iconUrl = 'sidebar/test-generator.png';
        this.openModalable(disabledFeatureModal);
    }

    openDisabledDrawingModeModal(): void {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.DRAW_MODE;
        disabledFeatureModal.iconUrl = 'draw-mode/icon-tool-drawing.png';
        this.openModal(disabledFeatureModal);
    }

    openDisabledChapterModal(): void {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.PAGE_FAVORITE;
        disabledFeatureModal.iconUrl = 'sidebar/sidebar-icon-kapitel.png';
        this.openModal(disabledFeatureModal);
    }

    openDisabledSiaModal(): void {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.LINK;
        disabledFeatureModal.iconUrl = 'sidebar-quicklinks/sia-logo.png';
        this.openModal(disabledFeatureModal);
    }

    openDisabledCalcuflowModal(): void {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.CALCUFLOW;
        disabledFeatureModal.iconUrl = 'sidebar-quicklinks/sidebar-icon-calcuflow.png';
        this.openModal(disabledFeatureModal);
    }

    openDisabledSchoolBagModal(): void {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.SCHOOL_BAG;
        disabledFeatureModal.iconUrl = 'sidebar/sidebar-icon-dateien.png';
        this.openModal(disabledFeatureModal);
    }

    openDisabledMaterialModal(): void {
        const disabledFeatureModal = new DisabledFeatureModalable();
        disabledFeatureModal.type = ModalService.MATERIALS;
        disabledFeatureModal.iconUrl = 'topbar-icon-materialien.png';
        this.openModal(disabledFeatureModal);
    }

    openRedirectModal(): void {
        const redirectModal = new RedirectModalComponent();
        this.openModal(redirectModal);
    }

    openVideoModal(modalable: Video): void {
        this.openModal(modalable);
    }

    openNoInternetModal(): void {
        const noInternetModal = new NoInternetModalComponent();
        this.openModal(noInternetModal);
    }

    getModalType(modalable: Modalable): string | QuickLinkType {
        if (modalable instanceof QuickLink || modalable instanceof DisabledFeatureModalable) {
            return modalable.type;
        }

        if (modalable instanceof Link) {
            return ModalService.LINK;
        }

        if (modalable instanceof Materials) {
            return ModalService.MATERIALS;
        }

        if (modalable instanceof Video) {
            return ModalService.VIDEO;
        }

        if (modalable instanceof Audio) {
            return ModalService.AUDIO;
        }

        if (modalable instanceof ModalableYedo) {
            return QuickLinkType.YEDO;
        }

        if (modalable instanceof ModalableEdusuite) {
            return QuickLinkType.EDU_SUITE;
        }

        return 'type';
    }

    checkNormal(modalable: Modalable): boolean {
        return this.checkRequiredCode(modalable, 'isPremium');
    }

    // eslint-disable-next-line complexity
    checkRequiredCode(modalable: Modalable, key = 'isPremiumPlus'): boolean {
        const type = this.getModalType(modalable);
        switch (type) {
            case ModalService.PHASE:
                return this.applicationStateService.bookConfig.bookPermissions.phaseLinks[key];
            case ModalService.E_SQUIRREL:
                return this.applicationStateService.bookConfig.bookPermissions.eSquirrelLinks[key];
            case ModalService.CALCUFLOW:
                return this.applicationStateService.bookConfig.bookPermissions.calcuflowLinks[key];
            case ModalService.GMT:
                return this.applicationStateService.bookConfig.bookPermissions.gmtLinks[key];
            case ModalService.YEDO:
                return this.applicationStateService.bookConfig.bookPermissions.siaLinks[key];
            case ModalService.DIGI_TRAINER_LINK:
                return this.applicationStateService.bookConfig.bookPermissions.exerciseSeries[key];
            case ModalService.SIA:
                return this.applicationStateService.bookConfig.bookPermissions.siaLinks[key];
            case ModalService.DIGI:
            case ModalService.OTHER:
                return this.applicationStateService.bookConfig.bookPermissions.quickLinks[key];
            case ModalService.TEST_GENERATOR:
            case ModalService.HSM:
                if ((modalable as QuickLink).link === 'hsmSidebar') {
                    return this.applicationStateService.bookConfig.bookPermissions.homework[key];
                }

                return this.applicationStateService.bookConfig.bookPermissions.hsmLinks[key];
            case ModalService.EXPORT_PAGES:
            case ModalService.MATERIALS:
                return this.applicationStateService.bookConfig.bookPermissions.pageExport[key];
            case ModalService.LINK:
                // eslint-disable-next-line no-case-declarations
                const url = (modalable as Link).url;
                switch (true) {
                    case UrlHelper.isSameDomain(url, 'sia.education') || this.applicationStateService.bookConfig.bookSource === 'sia':
                        return this.applicationStateService.bookConfig.bookPermissions.siaLinks[key];
                    case UrlHelper.isSameDomain(url, 'eduvidual.at'):
                        return this.applicationStateService.bookConfig.bookPermissions.eduvidualLinks[key];
                    case UrlWhitelist.isOnWhiteList(url):
                        return this.applicationStateService.bookConfig.bookPermissions.link[key];
                    default:
                        return this.applicationStateService.bookConfig.bookPermissions.externalLinks[key];
                }
            case ModalService.VIDEO:
                return this.applicationStateService.bookConfig.bookPermissions.bookVideo[key];
            case ModalService.AUDIO:
                return this.applicationStateService.bookConfig.bookPermissions.bookAudio[key];
            case ModalService.HIGHLIGHT_TEXT:
                return this.applicationStateService.bookConfig.bookPermissions.textSelection[key];
            case ModalService.PAGE_FAVORITE:
                return this.applicationStateService.bookConfig.bookPermissions.pageFavorite[key];
            case ModalService.DRAW_MODE:
                return this.applicationStateService.bookConfig.bookPermissions.pageDrawing[key];
            case ModalService.SEARCH:
                return this.applicationStateService.bookConfig.bookPermissions.pageTexts[key];
            case ModalService.CREATE_NOTE:
                return this.applicationStateService.bookConfig.bookPermissions.rect[key];
            case ModalService.NOTES:
                return this.applicationStateService.bookConfig.bookPermissions.rect[key];
            case ModalService.DIGBI:
                return this.applicationStateService.bookConfig.bookPermissions.digbi[key];
            case ModalService.MATHION:
                return this.applicationStateService.bookConfig.bookPermissions.mathion[key];
            case ModalService.MATHE4ALLE:
                return this.applicationStateService.bookConfig.bookPermissions.mathe4Alle[key];
            case ModalService.MATHE_GYM:
                return this.applicationStateService.bookConfig.bookPermissions.matheGym[key];
            case ModalService.SCHOOL_BAG:
            default:
                return false;
        }
    }

    checkPremiumPlus(modalable: Modalable): boolean {
        return this.checkRequiredCode(modalable);
    }

    private openModalable(modalable: Modalable): void {
        this.openModalSubject.next(modalable);
    }

    private openModalById(modalId: string): void {
        if (!this.modals.has(modalId)) {
            console.error('Modal not registered');
        }
        const modal = this.modals.get(modalId);
        if (modal.canBypassCheck() || modalId === DisabledFeatureModalable.MODAL_ID || modalId === RedirectModalComponent.MODAL_ID) {
            // @ts-ignore
            bootstrap.Modal.getOrCreateInstance(`#${modal.getId()}`, modal.getOptions()).show();

            const modalElement = ($(`#${modal.getId()}`) as any);
            modalElement.one('hidden.bs.modal', ele => {
                modal.setOpen(false);
            });
        } else {
            this.openDisabledFeatureModal();
        }
    }
}
